import { GeneralConfigurationType } from '@consolidocs-ui/types';
import { UserApi } from './UserApi';

/**
 * `general-configuration/` is a paginated list endpoint, but the backend has
 * also been observed returning the single record directly under `data` (same
 * inconsistency GlobalSettingsApi documents). Accept both and unwrap to one
 * record — every caller wants a single named configuration.
 */
interface GeneralConfigurationByNameResponse {
  data?: GeneralConfigurationType | GeneralConfigurationType[] | null;
  count?: number;
  next?: string | null;
  previous?: string | null;
}

export interface OrganizationConfiguration {
  id: string;
  folder_config: string;
}

export interface OrganizationConfigurationResponse {
  data: OrganizationConfiguration;
}

export interface UpdateOrganizationConfigurationPayload {
  folder_config: string;
}

// General Configuration API Services - Inject into the existing UserApi
export const GeneralConfigurationApi = UserApi.injectEndpoints({
  endpoints: (builder) => ({
    getGeneralConfigurationByName: builder.query<
      GeneralConfigurationType | null,
      string
    >({
      query: (name) => ({
        url: 'general-configuration/',
        params: {
          name,
          page: 1,
          page_size: 1,
        },
      }),
      transformResponse: (
        response: GeneralConfigurationByNameResponse
      ): GeneralConfigurationType | null => {
        const data = response?.data;
        if (!data) return null;
        return Array.isArray(data) ? data[0] ?? null : data;
      },
      providesTags: ['general-configuration'],
    }),
    getOrganizationConfiguration: builder.query<
      OrganizationConfigurationResponse,
      void
    >({
      query: () => ({
        url: 'folder-configuration/',
      }),
      providesTags: ['organization-configuration'],
    }),
    updateOrganizationConfiguration: builder.mutation<
      OrganizationConfigurationResponse,
      UpdateOrganizationConfigurationPayload
    >({
      query: (payload) => ({
        url: 'folder-configuration/',
        method: 'PUT',
        data: payload,
      }),
      invalidatesTags: ['organization-configuration'],
    }),
  }),
});

export const {
  useGetGeneralConfigurationByNameQuery,
  useLazyGetGeneralConfigurationByNameQuery,
  useGetOrganizationConfigurationQuery,
  useUpdateOrganizationConfigurationMutation,
} = GeneralConfigurationApi;